/**
 * Rôles et capacités (§14). Chaque membre porte un rôle : un rôle de base ou un
 * rôle d'entreprise, dont les capacités sont les seuls droits du membre (plus
 * d'exceptions individuelles depuis le 13/10/2026). L'autorisation est TOUJOURS
 * vérifiée côté serveur.
 */

export const INTERNAL_ROLES = ['patron', 'dispatcher', 'technicien'] as const;
export type InternalRole = (typeof INTERNAL_ROLES)[number];

export const EXTERNAL_ROLES = ['client', 'sous_traitant'] as const;
export type ExternalRole = (typeof EXTERNAL_ROLES)[number];

/** Rôle plateforme, hors tenant. */
export const PLATFORM_ROLES = ['super_admin'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export type Role = InternalRole | ExternalRole | PlatformRole;

/** Rôles des contacts client (Q84). */
export const CUSTOMER_CONTACT_ROLES = ['administrateur', 'technique', 'facturation'] as const;
export type CustomerContactRole = (typeof CUSTOMER_CONTACT_ROLES)[number];

/** Capacités applicatives (matrice §14) — étendues par domaine. */
export const CAPABILITIES = [
  // Planning
  'planning.view_all',
  'planning.edit_all',
  'planning.edit_own',
  'planning.view_peers', // désactivable par le patron (Q170)
  // Interventions
  'intervention.create_any',
  'intervention.create_own',
  'intervention.close_assigned',
  'intervention.close_any',
  'intervention.reopen',
  'intervention.cancel',
  // Finances
  'pricing.view_sale_prices',
  'pricing.view_margins', // inclut prix d'achat et coûts internes
  'quote.manage',
  'quote.send_within_limits',
  'invoice.manage',
  'invoice.view',
  'payment.record',
  // Stock
  'stock.manage',
  'stock.consume_transfer',
  'stock.approve_adjustments',
  'purchase.propose',
  'purchase.approve',
  // Factures reçues et dépenses (15/10/2026) : saisir les siennes, gérer toutes celles
  // de l'entreprise.
  'expense.record',
  'expense.manage',
  // Clients / équipements
  'customer.manage',
  'customer.view',
  'equipment.manage',
  'equipment.history.view',
  'equipment.history.add_official',
  // Contrats
  'contract.manage',
  'contract.view',
  // Fluides / conformité
  'fluids.record',
  'compliance.validate',
  // Temps
  'time.record_own',
  'time.correct_any',
  'time.lock_periods',
  'time.review_estimates',
  // Administration tenant
  'users.manage',
  'settings.manage',
  'accounting.manage_integration',
  'audit.view_full', // patron uniquement (Q87)
  'absence.approve',
  'task.assign', // donner une tâche à un autre membre (chacun s'en crée librement)
  'conflict.arbitrate', // conflits hors ligne (Q183)
  // RH — jamais dispatcher (Q83)
  'hr.view',
  // Avis clients : réservés au patron (décision du 12/10/2026), jamais aux équipes
  // dont ils peuvent parler.
  'feedback.view',
  // Page Analyses : patron par défaut, ouvrable à un rôle ou à une personne.
  // Chaque domaine exige en plus le droit de ce qu'il agrège.
  'analytics.view',
  // Identité légale de l'entreprise (raison sociale, forme, SIRET, TVA, adresse) :
  // patron seul, avec vérification d'identité récente (décision du 18/10/2026).
  'company.legal_identity',
  // Messages (25/10/2026) : conversations entre membres et lecture des annonces, à
  // tous les rôles internes par défaut ; publier une annonce, au patron par défaut.
  'messaging.use',
  'announcement.publish',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

export const DEFAULT_ROLE_CAPABILITIES: Readonly<Record<InternalRole, readonly Capability[]>> = {
  patron: [
    'planning.view_all',
    'planning.edit_all',
    'planning.edit_own',
    'planning.view_peers',
    'intervention.create_any',
    'intervention.close_any',
    'intervention.reopen',
    'intervention.cancel',
    'pricing.view_sale_prices',
    'pricing.view_margins',
    'quote.manage',
    'invoice.manage',
    'invoice.view',
    'payment.record',
    'stock.manage',
    'stock.consume_transfer',
    'stock.approve_adjustments',
    'purchase.propose',
    'purchase.approve',
    'expense.record',
    'expense.manage',
    'customer.manage',
    'customer.view',
    'equipment.manage',
    'equipment.history.view',
    'equipment.history.add_official',
    'contract.manage',
    'contract.view',
    'fluids.record',
    'compliance.validate',
    'time.record_own',
    'time.correct_any',
    'time.lock_periods',
    'time.review_estimates',
    'users.manage',
    'settings.manage',
    'accounting.manage_integration',
    'audit.view_full',
    'absence.approve',
    'task.assign',
    'conflict.arbitrate',
    'hr.view',
    'feedback.view',
    'analytics.view',
    'company.legal_identity',
    'messaging.use',
    'announcement.publish',
  ],
  dispatcher: [
    'planning.view_all',
    'planning.edit_all',
    'planning.edit_own',
    'planning.view_peers',
    'intervention.create_any',
    'intervention.close_any',
    'intervention.reopen',
    'intervention.cancel',
    'pricing.view_sale_prices',
    'pricing.view_margins', // commercial oui, RH non (Q83)
    'quote.manage',
    'invoice.manage',
    'invoice.view',
    'payment.record',
    'stock.manage',
    'stock.consume_transfer',
    'purchase.propose',
    'purchase.approve',
    'expense.record',
    'expense.manage',
    'customer.manage',
    'customer.view',
    'equipment.manage',
    'equipment.history.view',
    'equipment.history.add_official',
    'contract.manage',
    'contract.view',
    'fluids.record',
    'compliance.validate',
    'time.record_own',
    'time.correct_any',
    'absence.approve',
    'task.assign',
    'messaging.use',
  ],
  technicien: [
    'planning.edit_own',
    'planning.view_peers', // par défaut, désactivable (Q170)
    'intervention.create_own',
    'intervention.close_assigned',
    'pricing.view_sale_prices', // jamais marges/prix d'achat (Q82)
    'quote.send_within_limits',
    'stock.consume_transfer',
    'purchase.propose',
    'expense.record', // ses propres tickets (repas, carburant…), jamais ceux des autres
    'customer.view',
    'equipment.history.view',
    'equipment.history.add_official',
    'contract.view',
    'fluids.record',
    'time.record_own',
    'messaging.use',
  ],
};

export function roleHasCapability(role: InternalRole, cap: Capability): boolean {
  return DEFAULT_ROLE_CAPABILITIES[role].includes(cap);
}

// ---------------------------------------------------------------------------
// Rôles d'entreprise et hiérarchie
//
// Les trois rôles de base restent le socle (interface, obligations MFA, règles
// métier). Une entreprise peut créer ses propres rôles par-dessus : un nom, un
// niveau hiérarchique et une liste de capacités. Chaque membre a UN rôle.
// ---------------------------------------------------------------------------

/** Niveau des rôles de base. Plus le nombre est grand, plus on est haut. */
export const BUILTIN_ROLE_RANKS: Readonly<Record<InternalRole, number>> = {
  patron: 100,
  dispatcher: 50,
  technicien: 10,
};

/** Rang interne du Patron, toujours tout en haut ; les autres rôles vont de 99 à 1. */
export const PATRON_RANK = 100;

/** Un rôle d'entreprise se place toujours sous le patron. */
export const CUSTOM_ROLE_MIN_RANK = 1;
export const CUSTOM_ROLE_MAX_RANK = 99;

/** Socle d'un rôle d'entreprise : interface bureau ou terrain. Jamais patron. */
export const CUSTOM_ROLE_BASES = ['dispatcher', 'technicien'] as const;
export type CustomRoleBase = (typeof CUSTOM_ROLE_BASES)[number];

/**
 * Sur qui portent les droits « d'équipe » (planning, temps, absences et tâches des autres) :
 * `below` = seulement les membres d'un niveau strictement inférieur ;
 * `all` = toute l'entreprise (cas du bureau qui planifie aussi le patron).
 */
export const MANAGE_SCOPES = ['below', 'all'] as const;
export type ManageScope = (typeof MANAGE_SCOPES)[number];

export const BUILTIN_MANAGE_SCOPES: Readonly<Record<InternalRole, ManageScope>> = {
  patron: 'all',
  dispatcher: 'all',
  technicien: 'below',
};

/** Capacités qu'aucun rôle d'entreprise ne peut porter (Q83, Q87). */
export const PATRON_ONLY_CAPABILITIES: readonly Capability[] = [
  'audit.view_full', 'hr.view', 'feedback.view', 'company.legal_identity',
];

/**
 * Droits sensibles (décision 20 du 19/10/2026) : membres, réglages, factures et
 * paiements, comptabilité. Un compte qui en porte un, par son rôle quel qu'en
 * soit le nom ou le socle, doit avoir la double authentification. L'API le
 * vérifie sur les droits effectifs ; l'écran des rôles ne fait que l'annoncer.
 */
export const MFA_REQUIRED_CAPABILITIES: readonly Capability[] = [
  'users.manage', 'settings.manage', 'invoice.manage', 'payment.record', 'accounting.manage_integration',
];

export function capabilitiesRequireMfa(capabilities: readonly Capability[]): boolean {
  return capabilities.some((cap) => MFA_REQUIRED_CAPABILITIES.includes(cap));
}

/**
 * Comptable invité par l'entreprise, place gratuite (« conseiller », décisions du
 * 25/10/2026) : consulter factures, dépenses, factures reçues, clients, contrats,
 * analyses, prix de vente et marges ; enregistrer les paiements, gérer les dépenses
 * et l'intégration comptable ; créer et émettre factures et avoirs. Jamais devis,
 * planning, interventions, temps, stock, fluides, équipements, membres ni réglages.
 * La base borne le rôle système `conseiller` à cette liste
 * (`app_private.advisor_capabilities`, migration 202610252143) ; il porte toujours
 * un droit sensible, donc la double authentification.
 */
export const ADVISOR_CAPABILITIES: readonly Capability[] = [
  'invoice.view', 'invoice.manage', 'payment.record', 'expense.record', 'expense.manage',
  'customer.view', 'contract.view', 'analytics.view', 'pricing.view_sale_prices',
  'pricing.view_margins', 'accounting.manage_integration',
];

/** Places gratuites de conseiller par entreprise, sauf plafond relevé par la plateforme. */
export const DEFAULT_ADVISOR_LIMIT = 2;

/** Fonction déclarée par le comptable sur sa fiche. */
export const ACCOUNTANT_FUNCTIONS = ['expert_comptable', 'collaborateur'] as const;
export type AccountantFunction = (typeof ACCOUNTANT_FUNCTIONS)[number];

/** État de la fiche comptable d'un compte. */
export const ACCOUNTANT_STATUSES = ['en_verification', 'verifie', 'refuse', 'retire'] as const;
export type AccountantStatus = (typeof ACCOUNTANT_STATUSES)[number];

/** Code NAF de l'activité principale attendue d'un cabinet comptable. */
export const ACCOUNTANT_NAF = '69.20Z';

/**
 * Un même SIREN porté par au moins ce nombre de comptes est signalé dans la console :
 * un cabinet réel a plusieurs collaborateurs, au-delà la plateforme regarde de plus près.
 */
export const ACCOUNTANT_SIREN_ALERT = 10;

/**
 * SIREN : neuf chiffres et clé de Luhn (INSEE). La Poste (356000000) fait
 * exception : sa clé est la somme des chiffres multiple de 5.
 */
export function isValidSiren(value: string): boolean {
  if (!/^[0-9]{9}$/.test(value)) return false;
  if (value === '356000000') return true;
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    let digit = Number(value[8 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

export function isRoleAssignableCapability(cap: Capability): boolean {
  return !PATRON_ONLY_CAPABILITIES.includes(cap);
}

/** Position d'un membre dans la hiérarchie de son entreprise. */
export interface HierarchyPosition {
  baseRole: InternalRole;
  rank: number;
  manageScope: ManageScope;
}

/**
 * Administrer un compte (rôle, droits, accès) : uniquement un membre d'un niveau
 * STRICTEMENT inférieur. Les patrons, tout en haut, s'administrent entre eux —
 * il faut que quelqu'un le puisse. Personne d'autre ne s'administre soi-même.
 */
export function canAdminister(actor: HierarchyPosition, target: { rank: number }): boolean {
  return actor.baseRole === 'patron' || actor.rank > target.rank;
}

/**
 * Diriger le travail d'un membre (son planning, ses temps, ses absences, ses tâches) quand la
 * capacité correspondante est détenue. On agit toujours sur soi-même.
 */
export function canDirect(
  actor: HierarchyPosition,
  target: { rank: number },
  isSelf: boolean,
): boolean {
  return isSelf || actor.manageScope === 'all' || actor.rank > target.rank;
}

export interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  baseRole: CustomRoleBase;
  rank: number;
  manageScope: ManageScope;
  capabilities: readonly Capability[];
}

/**
 * Modèles proposés à la création d'un rôle. Ce sont des points de départ :
 * l'entreprise renomme, change le niveau et coche ce qu'elle veut. Rien n'est
 * installé d'office.
 */
export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  {
    key: 'chef_equipe',
    name: 'Chef d’équipe',
    description:
      'Technicien qui organise aussi le travail des techniciens placés sous lui : planning, temps et absences.',
    baseRole: 'technicien',
    rank: 30,
    manageScope: 'below',
    capabilities: [
      ...DEFAULT_ROLE_CAPABILITIES.technicien,
      'planning.view_all',
      'planning.edit_all',
      'intervention.create_any',
      'intervention.close_any',
      'time.correct_any',
      'absence.approve',
      'task.assign',
    ],
  },
  {
    key: 'apprenti',
    name: 'Apprenti',
    description: 'Accompagne sur le terrain : consulte, saisit son temps, ne crée ni ne clôture seul.',
    baseRole: 'technicien',
    rank: 5,
    manageScope: 'below',
    capabilities: [
      'planning.view_peers',
      'stock.consume_transfer',
      'customer.view',
      'equipment.history.view',
      'time.record_own',
      'messaging.use',
    ],
  },
  {
    key: 'bureau',
    name: 'Bureau',
    description: 'Planifie toute l’équipe et gère clients, devis et factures, sans les réglages de l’entreprise.',
    baseRole: 'dispatcher',
    rank: 50,
    manageScope: 'all',
    capabilities: DEFAULT_ROLE_CAPABILITIES.dispatcher,
  },
  {
    key: 'comptabilite',
    name: 'Comptabilité',
    description:
      'Devis, factures, règlements, factures reçues et analyses des ventes, sans le planning ni le terrain.',
    baseRole: 'dispatcher',
    rank: 40,
    manageScope: 'below',
    capabilities: [
      'pricing.view_sale_prices',
      'pricing.view_margins',
      'quote.manage',
      'invoice.manage',
      'invoice.view',
      'payment.record',
      'customer.view',
      'contract.view',
      'time.record_own',
      'analytics.view',
      'expense.record',
      'expense.manage',
      'messaging.use',
    ],
  },
];

// --- Tâches internes ----------------------------------------------------------
export const TASK_PRIORITIES = ['basse', 'normale', 'haute', 'urgente'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/**
 * Objectifs (décision du propriétaire du 15 octobre 2026) : une tâche avec une
 * heure limite. Bornes communes à l'écran et au serveur.
 */
export const OBJECTIVE_LIMITS = {
  /** « C'est fini quand… » : une phrase, pas un cahier des charges. */
  doneCriteriaMax: 300,
  /** Raison facultative de « Je bloque ». */
  blockedReasonMax: 300,
  /** Rappel doux à la personne, avant l'heure limite. */
  reminderMinutesBefore: 30,
} as const;

/**
 * Sorte de fichier d'une photo de preuve d'objectif, posée à l'envoi par l'écran
 * des objectifs. Seule une telle photo prouve un objectif, et elle n'est montrée
 * qu'à la personne et à celui qui a donné l'objectif : une photo d'intervention
 * (`photo`) ne devient jamais une preuve, ni cachée au reste de l'équipe.
 */
export const OBJECTIVE_PROOF_FILE_KIND = 'preuve_objectif';

/**
 * État d'un objectif tel qu'on le lit au débrief du soir : tenu (fait avant
 * l'heure), fait après l'heure, en cours, bloqué, ou non atteint (l'heure est
 * passée sans qu'il soit fait). Jamais une sanction : un repère pour en parler.
 */
export type ObjectiveState = 'tenu' | 'fait_apres' | 'en_cours' | 'bloque' | 'non_atteint';

export function objectiveState(
  task: { status: 'a_faire' | 'faite'; due_at: string | Date; done_at: string | Date | null; blocked_at: string | Date | null },
  now: Date = new Date(),
): ObjectiveState {
  const due = new Date(task.due_at).getTime();
  if (task.status === 'faite') {
    return task.done_at && new Date(task.done_at).getTime() <= due ? 'tenu' : 'fait_apres';
  }
  if (task.blocked_at) return 'bloque';
  return now.getTime() > due ? 'non_atteint' : 'en_cours';
}
